import type { AdminRefundListItem, AdminRefundListResponse } from '@inflip/contracts/admin';
import {
  ORDER_STATUS_LABELS,
  OrderStatus,
  PayoutClawbackStatus,
  RefundStatus,
} from '@inflip/shared';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DataTable } from '../components/data-table';
import { DateRangeFilter } from '../components/date-range-filter';
import {
  Button,
  Chip,
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  PageHeader,
  PromptDialog,
} from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import {
  ORDER_STATUS_TEXT,
  PAYOUT_CLAWBACK_STATUS_META,
  REFUND_STATUS_META,
} from '../lib/status-ui';
import { formatDateTime } from '../lib/time-zone';
import { useListViewState } from '../lib/use-list-view-state';
import { notifyPendingWorkChanged } from '../lib/use-pending-work';

const PAGE_SIZE = 20;

type DialogState =
  | null
  | { kind: 'approve'; refund: AdminRefundListItem }
  | { kind: 'reject'; refund: AdminRefundListItem }
  | { kind: 'complete'; refund: AdminRefundListItem };

/** 환불 큐 — 신청 접수부터 지급 완료까지 한 화면에서 처리 */
export function RefundsPage() {
  const { session } = useAdminAuth();
  const [filter, setFilter] = useListViewState<RefundStatus | ''>('status', RefundStatus.REQUESTED);
  // 정산 회수 상태 — 대시보드 "회수 기한 지남"에서 들어오면 기한 지난 회수가 걸린 환불만
  const [clawback, setClawback] = useListViewState<PayoutClawbackStatus | ''>('clawback', '');
  const [page, setPage] = useListViewState('page', 1);
  // 기간 — 환불 요청일 기준. 기본은 전체 (처리할 요청이 기간 밖으로 숨지 않게)
  const [dateFrom, setDateFrom] = useListViewState('from', '');
  const [dateTo, setDateTo] = useListViewState('to', '');
  const [result, setResult] = useState<AdminRefundListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(null);

  const load = useCallback(() => {
    if (!session) return Promise.resolve();
    return api
      .listRefunds(session.accessToken, {
        page,
        pageSize: PAGE_SIZE,
        ...(filter ? { status: filter } : {}),
        ...(clawback ? { clawbackStatus: clawback } : {}),
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
      })
      .then(setResult)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [session, filter, clawback, page, dateFrom, dateTo]);

  // 칩 옆 숫자 — 아직 돈이 나가지 않은 환불(요청됨·승인됨). 기간 필터와 무관하게 전체, 목록을 다시 불러올 때 같이
  const [todo, setTodo] = useState<Partial<Record<RefundStatus, number>>>({});
  const loadTodo = useCallback(() => {
    if (!session) return;
    void Promise.all(
      [RefundStatus.REQUESTED, RefundStatus.APPROVED].map((status) =>
        api
          .listRefunds(session.accessToken, { status, pageSize: 1 })
          .then((r): [RefundStatus, number] => [status, r.total]),
      ),
    )
      .then((entries) => setTodo(Object.fromEntries(entries)))
      .catch(() => {}); // 숫자는 부가 정보 — 실패해도 목록은 그대로
  }, [session]);

  useEffect(() => {
    void load();
    loadTodo();
  }, [load, loadTodo]);

  async function run(fn: () => Promise<unknown>) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await fn();
      setDialog(null);
      await load();
      loadTodo();
      // 사이드바 빨간 점도 바로 다시 센다
      notifyPendingWorkChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // 그사이 다른 어드민·회원이 상태를 바꿔 거절됐을 수 있다 — 지금 상태로 다시 그린다
      setDialog(null);
      await load();
    } finally {
      setPending(false);
    }
  }

  const token = session?.accessToken;

  const columns = useMemo<ColumnDef<AdminRefundListItem>[]>(
    () => [
      {
        id: 'status',
        header: '상태',
        size: 100,
        cell: ({ row }) => (
          <span className={`text-xs font-bold ${REFUND_STATUS_META[row.original.status].cls}`}>
            {REFUND_STATUS_META[row.original.status].label}
          </span>
        ),
      },
      {
        // 환불 번호 — 누르면 이 환불의 처리 기록(승인·반려·완료한 운영자·시각)으로
        id: 'refund',
        header: '환불 번호',
        cell: ({ row }) => (
          <Link
            to="/audit-logs"
            search={{ q: row.original.id }}
            title="이 환불의 처리 기록 보기"
            className="font-mono text-xs font-medium text-brand hover:underline"
          >
            {row.original.id.slice(0, 8)}
          </Link>
        ),
      },
      {
        id: 'order',
        header: '주문',
        cell: ({ row }) => (
          <>
            <Link
              to="/orders/$orderId"
              params={{ orderId: row.original.orderId }}
              className="font-mono text-xs font-medium text-brand hover:underline"
            >
              {row.original.orderId.slice(0, 8)}
            </Link>
            <div
              className={`mt-0.5 text-[11px] font-semibold ${ORDER_STATUS_TEXT[row.original.orderStatus]}`}
            >
              {ORDER_STATUS_LABELS[row.original.orderStatus]}
            </div>
          </>
        ),
      },
      {
        id: 'buyer',
        header: '구매자',
        cell: ({ row }) => (
          <>
            <div className="truncate font-semibold">{row.original.buyerName ?? '—'}</div>
            <div className="truncate text-xs text-muted-foreground">
              {row.original.buyerEmail ?? ''}
            </div>
          </>
        ),
      },
      {
        id: 'amount',
        header: '금액',
        meta: { align: 'right', tdClass: 'tabular-nums font-bold whitespace-nowrap' },
        cell: ({ row }) => `${row.original.amount.toLocaleString('ko-KR')}원`,
      },
      {
        id: 'reason',
        header: '사유 · 수취 계좌',
        cell: ({ row }) => (
          <>
            <div className="truncate">{row.original.reason}</div>
            {row.original.bankAccount && (
              <div className="truncate text-xs text-muted-foreground">
                {row.original.bankAccount.bank} {row.original.bankAccount.accountNumber} (
                {row.original.bankAccount.holder})
              </div>
            )}
            {row.original.rejectReason && (
              <div className="truncate text-xs text-rose-600">
                거절: {row.original.rejectReason}
              </div>
            )}
          </>
        ),
        meta: { tdClass: 'text-sm' },
      },
      {
        id: 'createdAt',
        header: '신청일',
        cell: ({ row }) => formatDateTime(row.original.createdAt),
        meta: { tdClass: 'whitespace-nowrap text-xs text-muted-foreground' },
      },
      {
        id: 'actions',
        header: '액션',
        minSize: 150,
        meta: { align: 'right' },
        cell: ({ row }) => {
          const r = row.original;
          return (
            <div className="flex justify-end gap-1.5">
              {r.status === RefundStatus.REQUESTED && (
                <>
                  <Button
                    size="sm"
                    disabled={pending}
                    onClick={() => setDialog({ kind: 'approve', refund: r })}
                  >
                    승인
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={pending}
                    onClick={() => setDialog({ kind: 'reject', refund: r })}
                  >
                    거절
                  </Button>
                </>
              )}
              {r.status === RefundStatus.APPROVED &&
                (r.clawback?.status === PayoutClawbackStatus.PENDING ||
                r.clawback?.status === PayoutClawbackStatus.OVERDUE ? (
                  // 정산 지급 뒤 환불 — 판매자 회수 확인(주문 상세)이 먼저
                  <Link
                    to="/orders/$orderId"
                    params={{ orderId: r.orderId }}
                    className={`text-xs font-bold hover:underline ${PAYOUT_CLAWBACK_STATUS_META[r.clawback.status].cls}`}
                    title="판매자 회수를 확인한 뒤 지급할 수 있어요"
                  >
                    {PAYOUT_CLAWBACK_STATUS_META[r.clawback.status].label} →
                  </Link>
                ) : (
                  <Button
                    size="sm"
                    disabled={pending}
                    onClick={() => setDialog({ kind: 'complete', refund: r })}
                  >
                    지급 완료
                  </Button>
                ))}
            </div>
          );
        },
      },
    ],
    [pending],
  );

  if (!session || !token) return null;

  const rows = result?.items ?? [];

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="환불 관리"
        description={
          result
            ? `전체 ${result.total.toLocaleString('ko-KR')}건`
            : '환불 신청 접수부터 지급까지 처리해요'
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        <Chip
          active={filter === '' && !clawback}
          onClick={() => {
            setPage(1);
            setFilter('');
            setClawback('');
          }}
        >
          전체
        </Chip>
        {Object.entries(REFUND_STATUS_META).map(([k, v]) => (
          <Chip
            key={k}
            active={filter === k && !clawback}
            onClick={() => {
              setPage(1);
              setFilter(k as RefundStatus);
              setClawback('');
            }}
          >
            {v.label}
            {(todo[k as RefundStatus] ?? 0) > 0 && (
              <span
                className={`ml-1 tabular-nums ${filter === k && !clawback ? 'text-background/70' : 'font-bold text-rose-600'}`}
              >
                {todo[k as RefundStatus]!.toLocaleString('ko-KR')}
              </span>
            )}
          </Chip>
        ))}
        <Chip
          active={clawback === PayoutClawbackStatus.OVERDUE}
          onClick={() => {
            setPage(1);
            setFilter('');
            setClawback(PayoutClawbackStatus.OVERDUE);
          }}
        >
          회수 기한 지남
        </Chip>
        <DateRangeFilter
          from={dateFrom}
          to={dateTo}
          onChange={(from, to) => {
            setPage(1);
            setDateFrom(from);
            setDateTo(to);
          }}
        />
      </div>

      {error && <ErrorBanner message={error} />}

      {result && rows.length === 0 ? (
        <EmptyState message="해당 상태의 환불 건이 없어요." />
      ) : (
        <DataTable data={rows} columns={columns} getRowId={(r) => r.id} />
      )}

      {result && result.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            이전
          </Button>
          <span className="text-sm tabular-nums text-muted-foreground">
            {result.page} / {result.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= result.totalPages}
            onClick={() => setPage(page + 1)}
          >
            다음
          </Button>
        </div>
      )}

      {dialog?.kind === 'approve' && (
        <ConfirmDialog
          title="환불을 승인할까요?"
          description={
            dialog.refund.orderStatus === OrderStatus.PAYOUT_COMPLETED ||
            dialog.refund.orderStatus === OrderStatus.TERMINATED
              ? `${dialog.refund.amount.toLocaleString('ko-KR')}원 — 정산이 이미 지급된 주문이면 판매자에게 회수 요청(기한 14일)이 가요. 회수를 확인한 뒤 송금하고 지급 완료 처리해 주세요.`
              : `${dialog.refund.amount.toLocaleString('ko-KR')}원 — 승인 후 실제 송금을 마치면 지급 완료 처리해주세요.`
          }
          confirmLabel="승인"
          busy={pending}
          onConfirm={() => void run(() => api.approveRefund(token, dialog.refund.id))}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'reject' && (
        <PromptDialog
          title="환불을 거절할까요?"
          description="거절 사유는 구매자에게도 보여요."
          placeholder="거절 사유"
          submitLabel="거절"
          danger
          busy={pending}
          onSubmit={(reason) =>
            void run(() => api.rejectRefund(token, dialog.refund.id, { reason }))
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'complete' && (
        <ConfirmDialog
          title="지급 완료 처리할까요?"
          description={`${dialog.refund.amount.toLocaleString('ko-KR')}원 — 실제 송금을 마친 뒤 눌러주세요. 전액 환불이면 주문이 환불 종결되고 라이선스가 회수돼요.`}
          confirmLabel="지급 완료"
          busy={pending}
          onConfirm={() => void run(() => api.completeRefund(token, dialog.refund.id))}
          onClose={() => setDialog(null)}
        />
      )}
    </main>
  );
}
